import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { useSubscriptionsContext } from "@/contexts/subscriptions";
import { useShopBook } from "@/shared/hooks/useShopBook";
import { makeChapterUrl } from "@/utils/routes/routes";
import { makeHashInitActionListen } from "@/shared/utils/init-actions";
import { Button, ButtonQRCode, LinkButton, SharePopup } from "@/components/index";
import { getCPanelUrl, getMainAppHost, makeResourceUrl } from "@/shared/utils/url/url";
import {
  BUTTON_COLOR,
  BUTTON_SIZES,
  BUTTON_VARIANT,
} from "@/shared/components/Button/Button.constants";
import { useAudioContext } from "@/shared/contexts/AudioContext";
import { settingsActions } from "@/store/actions/settings.actions";
import { tocActions } from "@/store/actions/toc.actions";
import { selectorTocItems } from "@/shared/store/toc/selectors";
import { selectorSystemOnLine } from "@/shared/store/system/selectors";
import { SETTING } from "@/shared/utils/settings/constants";
import ShareMaterials, { mapTitleSharingMaterialHref } from "@/views/BookLanding/ShareMaterials";
import { libraryActions } from "@/store/actions";
import { usePagePropsContext } from "@/contexts/pageProps";
import { LANDING_SELECTORS } from "@/utils/landing/landing.constants";

import styles from "./BookActions.module.scss";

const ButtonSubscribe = ({ book }) => {
  const { t } = useTranslation();
  const { isSubscribedBook } = useSubscriptionsContext();

  const subscription = isSubscribedBook(book);

  return (
    <LinkButton
      isOnLineOnly
      target="_blank"
      variant={BUTTON_VARIANT.outline}
      color={Button.color.secondary}
      href={`${getCPanelUrl()}/subscriptions/add`}>
      {subscription ? t("subscribed") : t("subscribe")}
    </LinkButton>
  );
};

const BookActions = ({
  isOnlyReadButton,
  isLanding,
  buttonVariant = BUTTON_VARIANT.outline,
  buttonColor = BUTTON_COLOR.secondary,
  book,
  hasBtnShareMaterials,
  shareMaterialsTitleCustom,
  isBookInfoActions,
  forceShowBuyBook,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const { playChapterAndShowPlayer } = useAudioContext();
  const { isAvailable, shopPackage } = useShopBook(book);
  const { urlOrigin } = usePagePropsContext();

  const tocItems = useSelector(selectorTocItems);
  const onLine = useSelector(selectorSystemOnLine);

  const [isReadListenDisabled, setIsReadListenDisabled] = useState(false);

  const pageLink = urlOrigin + router.basePath + router.asPath.split("#")[0];

  const { id, title, code, description, buy_link } = book;
  const { mobi, mp3, epub, pdf } = book.files;
  const buyBookLink = forceShowBuyBook?.linkBuyBook || buy_link;

  const bookToc = book ? tocItems[book.book_id] : null;
  const bookHasToc = !!bookToc?.length;

  const getIdAndPositionToOpenBook = (isGonnaListen) => {
    return new Promise((resolve) => {
      dispatch(
        libraryActions.getIdAndPositionToOpenBook(
          id,
          isGonnaListen,
          true,
          true,
          true,
          true,
          resolve
        ),
      );
    });
  };

  const getChapterIdToRead = async () => {
    const res = await getIdAndPositionToOpenBook(false);
    return res.id;
  };

  const getPlayerDataToListen = () => {
    return getIdAndPositionToOpenBook(true);
  };

  const handleClickListen = async () => {
    const { id, position } = await getPlayerDataToListen();

    if (id) {
      playChapterAndShowPlayer(id, position);
      dispatch(settingsActions.updateSettings(SETTING.syncPlayerAndReader.id, true));
    }
  };

  const handleContextMenuListen = async () => {
    const { id, position } = await getPlayerDataToListen();

    return makeHashInitActionListen(id, position);
  };

  const handleClickRead = async () => {
    dispatch(settingsActions.updateSettings(SETTING.syncPlayerAndReader.id, true));

    if (onLine) {
      router.push(makeChapterUrl(await getChapterIdToRead()));
    } else {
      window.location.assign(makeChapterUrl(await getChapterIdToRead()));
    }
  };

  const handleContextMenuRead = async () => {
    return makeChapterUrl(await getChapterIdToRead());
  };

  // Fetches book TOC.
  useEffect(() => {
    if (!bookToc) {
      dispatch(tocActions.fetch(book.book_id));
    }
  }, [bookToc, book]);

  // Disable buttons with debounce, to prevent disabled styles blinking.
  // It's too noticeable on Book.Set Home page carousel,
  // when changing current book of the books set.
  useEffect(() => {
    let idTimer;

    if (!bookHasToc) {
      idTimer = setTimeout(() => {
        setIsReadListenDisabled(true);
      }, 1250);
    }

    return () => {
      clearTimeout(idTimer);

      setIsReadListenDisabled(false);
    };
  }, [bookHasToc]);

  // On unmount: cancel possible running getting id and position to open a book.
  useEffect(() => {
    return () => {
      dispatch(libraryActions.cancelGetIdAndPositionToOpenBook());
    };
  }, []);

  const readButton = (
    <Button
      className={LANDING_SELECTORS.readBookBtn.substring(1)}
      isContextMenuCustom
      disabled={isReadListenDisabled}
      onClick={handleClickRead}
      onContextMenu={handleContextMenuRead}>
      {t("read")}
    </Button>
  );
  const listenButton = (
    <Button
      id={LANDING_SELECTORS.listenBookBtn.substring(1)}
      isOnLineOnly
      isContextMenuCustom
      color={Button.color.secondary}
      onClick={handleClickListen}
      onContextMenu={handleContextMenuListen}
      disabled={!book.is_audiobook || isReadListenDisabled}>
      {t("listen")}
    </Button>
  );

  if (isOnlyReadButton) {
    return readButton;
  }

  if (isBookInfoActions) {
    return (
      <div className={styles["book-info-actions"]}>
        {readButton}
        {listenButton}
        <ButtonQRCode
          withLabelBeforeIcon={"QR CODE"}
          variant={Button.variant.default}
          size={"1.2rem"}
        />
      </div>
    );
  }

  const shareHref = mapTitleSharingMaterialHref[shareMaterialsTitleCustom ?? book.book_id] || "";

  return (
    <div
      className={classNames(styles["book-actions"], {
        [styles["book-actions_landing"]]: isLanding,
      })}>
      <div
        className={classNames(styles["book-details-actions-row"], {
          [styles["book-details-actions-row_landing"]]: isLanding,
        })}>
        {!isAvailable ? (
          <LinkButton
            isOnLineOnly
            href={getMainAppHost() + router.asPath}
            variant={Button.variant.default}
            color={Button.color.tertiary}>
            {t("buyBook")}
          </LinkButton>
        ) : (
          readButton
        )}
        {shopPackage && !isAvailable ? (
          <LinkButton
            isOnLineOnly
            target="_blank"
            href={getMainAppHost() + router.asPath}
            variant={BUTTON_VARIANT.outline}
            color={Button.color.tertiary}>
            {t("buySet")}
          </LinkButton>
        ) : (
          listenButton
        )}
      </div>
      <div
        className={classNames(styles["book-details-actions-row"], {
          [styles["book-details-actions-row_landing"]]: isLanding,
        })}>
        <LinkButton
          id={LANDING_SELECTORS.pdfBookBtn.substring(1)}
          isOnLineOnly
          isContextMenuCustom
          target="_blank"
          variant={buttonVariant}
          color={buttonColor}
          size={isLanding ? undefined : BUTTON_SIZES.small}
          href={makeResourceUrl(pdf)}
          disabled={!pdf}>
          {t("pdf")}
        </LinkButton>
        <LinkButton
          id={LANDING_SELECTORS.ePubBookBtn.substring(1)}
          isOnLineOnly
          isContextMenuCustom
          target="_blank"
          variant={buttonVariant}
          color={buttonColor}
          size={isLanding ? undefined : BUTTON_SIZES.small}
          href={makeResourceUrl(epub)}
          disabled={!epub}>
          {t("epub")}
        </LinkButton>
        {!isLanding && (
          <LinkButton
            isOnLineOnly
            variant={BUTTON_VARIANT.outline}
            color={Button.color.secondary}
            size={BUTTON_SIZES.small}
            href={makeResourceUrl(mobi)}
            disabled={!mobi}>
            {t("mobi")}
          </LinkButton>
        )}
        {!isLanding && (
          <LinkButton
            isOnLineOnly
            variant={BUTTON_VARIANT.outline}
            color={Button.color.secondary}
            size={BUTTON_SIZES.small}
            href={makeResourceUrl(mp3)}
            disabled={!mp3}>
            {t("mp3")}
          </LinkButton>
        )}
      </div>
      <div
        className={classNames(styles["book-details-actions-row"], {
          [styles["book-details-actions-row_landing"]]: isLanding,
          [styles["actions-row-only-buy"]]: !(hasBtnShareMaterials && shareHref),
        })}>
        {isLanding && buyBookLink && (
          <LinkButton
            isOnLineOnly
            isContextMenuCustom
            target="_blank"
            variant={buttonVariant}
            color={buttonColor}
            size={isLanding ? undefined : BUTTON_SIZES.medium}
            href={buyBookLink === null ? "" : buyBookLink}
            disabled={buyBookLink === null}>
            {t(forceShowBuyBook?.titleBuyBook || "buyPrintedBook")}
          </LinkButton>
        )}
        {hasBtnShareMaterials && shareHref ? (
          <ShareMaterials
            buttonVariant={buttonVariant}
            buttonColor={buttonColor}
            href={shareHref}
          />
        ) : // Don't show "share" button if there is no "buy" button - requirement.
          isLanding && !buyBookLink ? null : (
            <SharePopup
              url={pageLink}
              title={title}
              refCode={code}
              text={description}
              renderButton={({ show, onClick }) => {
                return (
                  <Button
                    style={{ width: "100%" }}
                    active={show}
                    variant={buttonVariant}
                    color={buttonColor}
                    size={isLanding ? undefined : BUTTON_SIZES.medium}
                    onClick={onClick}>
                    {t("share")}
                  </Button>
                );
              }}
            />
          )}
      </div>
      {!isLanding && (
        <div
          className={classNames(styles["book-details-actions-row"], {
            [styles["book-details-actions-row_landing"]]: isLanding,
          })}>
          <ButtonSubscribe book={book} />
        </div>
      )}
    </div>
  );
};

BookActions.propTypes = {
  isLanding: PropTypes.bool,
  buttonVariant: PropTypes.string,
  buttonColor: PropTypes.string,
  isOnlyReadButton: PropTypes.bool,
  book: PropTypes.object,
  hasBtnBuyBook: PropTypes.bool,
  hasBtnShareBook: PropTypes.bool,
  hasBtnShareMaterials: PropTypes.bool,
  shareMaterialsTitleCustom: PropTypes.string,
  urlOrigin: PropTypes.string,
  isBookInfoActions: PropTypes.bool,
  forceShowBuyBook: PropTypes.bool,
};

export default BookActions;
